import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, Subject } from 'rxjs';
import { VisibilityState } from './visibility-state.interface';

@Injectable()
export class ObserverService implements OnDestroy {
  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
  ) {
    if (!isPlatformBrowser(this.platformId)) {return}

    this.observer = new IntersectionObserver(this.onIntersection.bind(this), {
      root: null,
      rootMargin: '50% 0px 50% 0px',
      threshold: 0
    });
  }

  private observer: IntersectionObserver;
  private visibilitySubject = new Subject<VisibilityState>();
  private observedElements = new Map<HTMLElement, () => void>();

  private onIntersection(entries: IntersectionObserverEntry[]): void {
    entries.forEach(entry => {
      const element = entry.target as HTMLElement;
      this.visibilitySubject.next({
        element,
        isVisible: entry.isIntersecting,
        entry
      });
    });
  }

  observe(element: HTMLElement): Observable<VisibilityState> {
    if (!this.observedElements.has(element)) {
      this.observer.observe(element);
      this.observedElements.set(element, () => {
        this.observer.unobserve(element);
        this.observedElements.delete(element);
      });
    }

    return new Observable<VisibilityState>(subscriber => {
      const subscription = this.visibilitySubject.subscribe(state => {
        if (state.element === element) {
          subscriber.next(state);
        }
      });

      return () => {
        subscription.unsubscribe();

        if(!this.visibilitySubject.observed){
          const destroy = this.observedElements.get(element);
          destroy?.();
        }
      };
    });
  }

  ngOnDestroy(): void {
    this.observer.disconnect();
    this.visibilitySubject.complete();
    this.observedElements.clear();
  }
}
